import { useState, useEffect } from "react"
import ICustomer from "types/ICustomer"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlags } from "types/FeatureFlags"

export const useShowNotification = (user: ICustomer, verifyCookie: boolean) => {
    const [showNotification, setShowNotification] = useState<boolean>(false)

    // Get feature flag states for verification requirements
    const emailFlagEnabled = useFeatureFlagEnabled(
        FeatureFlags.EMAIL_VERIFICATION_REQUIRED
    )
    const mobileFlagEnabled = useFeatureFlagEnabled(
        FeatureFlags.MOBILE_VERIFICATION_REQUIRED
    )

    // Default to true (verification required) if flag is not explicitly set to false
    const isEmailVerificationRequired = emailFlagEnabled !== false
    const isMobileVerificationRequired = mobileFlagEnabled !== false
    const isVerificationRequired =
        isEmailVerificationRequired || isMobileVerificationRequired

    useEffect(() => {
        // Don't show notification if verification is not required by feature flags
        if (!isVerificationRequired) {
            setShowNotification(false)
            return
        }

        if (!user) {
            setShowNotification(false)
        } else {
            // Check if user meets verification requirements based on enabled flags
            let meetsRequirements = false

            if (isEmailVerificationRequired && isMobileVerificationRequired) {
                // Both flags enabled: either email OR phone verification satisfies
                meetsRequirements =
                    Boolean(user?.verification?.is_email_verified) ||
                    Boolean(user?.verification?.is_phone_verified)
            } else if (isEmailVerificationRequired) {
                // Only email flag enabled
                meetsRequirements = Boolean(
                    user?.verification?.is_email_verified
                )
            } else if (isMobileVerificationRequired) {
                // Only mobile flag enabled
                meetsRequirements = Boolean(
                    user?.verification?.is_phone_verified
                )
            }

            setShowNotification(!verifyCookie && !meetsRequirements)
        }
    }, [
        user,
        verifyCookie,
        isVerificationRequired,
        isEmailVerificationRequired,
        isMobileVerificationRequired,
    ])

    return { showNotification, setShowNotification }
}
